import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Optional,
  Output,
} from '@angular/core';
import { EntityProvider, EntityRepositoryService, SimpleAttachmentLinkService } from '@technocom/data';
import { DialogCenterService } from '@technocom/ui/dialogs';
import { Subscription, TeardownLogic } from 'rxjs';
import { NavigatorService } from '@technocom/core';
import { Localizable } from '../../../models/localizable/localizable';
import { Role } from '@app/shared/models/user/role';
import { SecurityService } from '@technocom/auth';

@Component({
  selector: 'app-preview-card',
  templateUrl: './preview-card.component.html',
  styleUrls: ['./preview-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PreviewCardComponent<T extends Localizable = Localizable> implements OnInit, OnDestroy {
  @Input()
  dataItem: T;

  get preview(): string {
    return this.attachService.create(
      `${this.isPublic ? '/public' : ''}${this.sourceUrl ?? this._repository.getRoot()}/${this.dataItem.id}/preview`,
    );
  }

  @Input()
  @Optional()
  sourceUrl: string;

  @Input()
  @Optional()
  withPreview = true;

  @Input()
  primary = false;

  @Input()
  editable = false;

  @Output()
  deleteEvent: EventEmitter<T> = new EventEmitter<T>();

  @Input()
  @Optional()
  customActions: {
    open?: (dataItem) => void;
    edit?: (dataItem) => void;
  };

  protected _repository: EntityRepositoryService;

  private _subscription = new Subscription();

  constructor(
    protected entityProvider: EntityProvider,
    protected dialogService: DialogCenterService,
    protected navigator: NavigatorService,
    private securityService: SecurityService,
    protected attachService: SimpleAttachmentLinkService,
  ) {}

  // Проверка контура, нужна для выбора способа получения локализации и доступа к инструментам редактимрования
  get isPublic(): boolean {
    return !this.securityService.canUserAccess([Role.OPR, Role.DATA_ADM]);
  }

  ngOnInit() {
    this._repository = this.entityProvider.getRepository(this.dataItem.getClass());
  }

  ngOnDestroy() {
    this._subscription.unsubscribe();
  }

  protected attach(logic: TeardownLogic) {
    this._subscription.add(logic);
  }

  remove() {
    this.attach(
      this.dialogService
        .confirm({ title: 'Внимание!', content: 'Вы хотите удалить выделенную запись?' })
        .subscribe(() => {
          this.dialogService
            .wait(
              this._repository.remove(
                this.dataItem,
                this.sourceUrl ? { url: this.sourceUrl + `/${this.dataItem.id}` } : void 0,
              ),
              300,
            )
            .subscribe(() => this.deleteEvent.emit(this.dataItem));
        }),
    );
  }

  open(edit: boolean = false) {
    if ((this.customActions?.open && !edit) || (this.customActions?.edit && edit)) {
      if (edit) this.customActions.edit(this.dataItem);
      else this.customActions.open(this.dataItem);
      return;
    }
    this.navigator.navigate([this.dataItem.getClientUrl(), edit && this.editable ? 'edit' : void 0].filter(i => !!i));
  }
}
